import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NcpbService } from '@core/services/ncpb.service';
import { APIService } from '@core/services';
import { EpDocumentItem } from '@core/interfaces/eu/ep-document';
import { DropdownComponent, DropdownOptions } from '@shared/components/dropdown/dropdown.component';
import { BrandNameSearchComponent } from '@shared/components/dropdown/search/brand-name-search/brand-name-search.component';
import { InputComponent, InputOptions } from '@shared/components';
import { RadiosComponent, RadiosOptions } from '@shared/components/radios/radios.component';
import { FILTER_NUMBERS_WITH_DECIMALS, FILTER_ONLY_NUMBERS } from '@core/constants';
import { InputTypes } from '@core/enums';
import { TranslateService, Translation } from '@ngx-translate/core';

@Component({
  selector: 'ncp-eu-ep',
  imports: [
    DatePipe,
    FormsModule,
    ProgressSpinner,
    DropdownComponent,
    InputComponent,
    RadiosComponent,
  ],
  templateUrl: './ep.component.html',
})
export class EpComponent implements OnInit {
  @ViewChild('reportsSection')
  private reportsSection?: ElementRef<HTMLDivElement>;

  private readonly ncpbService = inject(NcpbService);
  private readonly apiService = inject(APIService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);

  protected translations: Translation = this.translate.instant('patient.ps.medication');
  protected translations2: Translation = this.translate.instant('eu');

  protected loading = false;
  protected documentLoading = false;
  protected errorMessage = '';

  protected list: EpDocumentItem[] = [];
  protected pdfUrl?: SafeResourceUrl;
  protected htmlUrl?: SafeResourceUrl;

  protected showDispensationForm = false;
  protected dispenseIsRunning = false;
  protected dispensationResults = '';
  protected dispensationIsSuccess = false;
  protected dispensationContext: any = {};

  protected productSelectedItem: any = {};
  protected packageSelectedItem: any = {};
  protected quantityUnitSelectedItem: any = {};
  protected frequencyUnitSelectedItem: any = {};
  protected durationUnitSelectedItem: any = {};
  protected pharmacySelectedItem: any = {};

  private numberOfPackagesControl = new FormControl<number | string>('');
  private quantityControl = new FormControl<number | string>('');
  private frequencyControl = new FormControl<number | string>('');
  private durationControl = new FormControl<number | string>('');

  protected pharmacyDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationPharmacy',
    placeholder: 'default',
    name: 'pharmacies',
    label: {
      id: 'dispensationPharmacyLabel',
      text: 'pharmacy',
    },
    data: [],
    required: true,
  });

  protected productDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationBrandName',
    placeholder: 'default',
    name: 'brandNameProducts',
    label: {
      id: 'dispensationBrandNameLabel',
      text: 'brand_name',
    },
    data: [],
    initialData: [],
    required: true,
    searchComponent: BrandNameSearchComponent,
  });

  protected packageDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationPackageDescription',
    placeholder: 'default',
    name: 'packageDescriptions',
    label: {
      id: 'dispensationPackageDescriptionLabel',
      text: 'package_description',
    },
    data: [],
    required: true,
  });

  protected quantityUnitDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationQuantityUnit',
    placeholder: 'default',
    name: 'quantityUnits',
    label: {
      id: 'dispensationQuantityUnitLabel',
      text: 'units_intake_description',
    },
    data: [],
    required: true,
  });

  protected frequencyUnitDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationFrequencyUnit',
    placeholder: 'default',
    name: 'frequencyUnits',
    label: {
      id: 'dispensationFrequencyUnitLabel',
      text: 'time_unit_description',
    },
    data: [],
    required: true,
  });

  protected durationUnitDropdownOptions = new DropdownOptions<any>({
    id: 'dispensationDurationUnit',
    placeholder: 'default',
    name: 'durationUnits',
    label: {
      id: 'dispensationDurationUnitLabel',
      text: 'time_unit_description',
    },
    data: [],
    required: true,
  });

  protected numPackagesInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'dispensationNumPackages',
      name: 'dispensationNumPackages',
      label: {
        id: 'dispensationNumPackagesLabel',
        text: 'numberOfPackages',
      },
      type: InputTypes.number,
      pattern: FILTER_ONLY_NUMBERS,
      formControl: this.numberOfPackagesControl,
      onKeyDown: (event) => {
        if (['e', 'E', '+', '-'].includes(event.key)) {
          event.preventDefault();
        }
      },
      required: true,
      min: 1,
      step: 1,
    })
  );

  protected quantityInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'dispensationQuantity',
      name: 'dispensationQuantity',
      label: {
        id: 'dispensationQuantityLabel',
        text: 'quantity',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.quantityControl,
      onKeyDown: (event) => {
        if (['e', 'E', '+', '-'].includes(event.key)) {
          event.preventDefault();
        }
      },
      required: true,
      min: 1,
    })
  );

  protected frequencyInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'dispensationFrequency',
      name: 'dispensationFrequency',
      label: {
        id: 'dispensationFrequencyLabel',
        text: 'frequency_of_use',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.frequencyControl,
      onKeyDown: (event) => {
        if (['e', 'E', '+', '-'].includes(event.key)) {
          event.preventDefault();
        }
      },
      required: true,
      min: 1,
    })
  );

  protected durationInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'dispensationDuration',
      name: 'dispensationDuration',
      label: {
        id: 'dispensationDurationLabel',
        text: 'treatment_duration',
      },
      type: InputTypes.number,
      pattern: FILTER_NUMBERS_WITH_DECIMALS,
      formControl: this.durationControl,
      onKeyDown: (event) => {
        if (['e', 'E', '+', '-'].includes(event.key)) {
          event.preventDefault();
        }
      },
      required: true,
      min: 1,
    })
  );

  protected substitutionRadiosOptions = signal<RadiosOptions>(
    new RadiosOptions({
      id: 'dispensationSubstitution',
      options: [
        {
          value: 1,
          label: 'yes',
        },
      ],
      onInput: (value) => {
        this.dispensationForm.substitution = value === 1;
      },
    })
  );

  protected dispensationForm: any = {
    productNameDispensation: '',
    atcCode: '',
    atcDescription: '',
    atcCodeName: '',
    strength: '',
    drugid: '',
    productPackage: '',
    packnr: '',
    pharmacy_id: '',
    number_of_packages: '',
    quantity: '',
    quantity_unit: '',
    frequency_of_intake: '',
    frequency_of_intake_unit: '',
    duration: '',
    duration_unit: '',
    substitution: false,
  };

  protected formdata: {
    prescriptionId?: string;
    dispensationPINCode?: string;
  } = {};

  protected selectedCountry = JSON.parse(sessionStorage.getItem('selectedCountry') || '{}');

  protected patient = JSON.parse(sessionStorage.getItem('euPatient') || '{}');

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => {
      this.translations = this.translate.instant('patient.ps.medication');
    });

    this.translate.onLangChange.subscribe(() => {
      this.translations2 = this.translate.instant('eu');
    });

    this.numberOfPackagesControl.valueChanges.subscribe((value) => {
      this.dispensationForm.number_of_packages = value ?? '';
    });

    this.quantityControl.valueChanges.subscribe((value) => {
      this.dispensationForm.quantity = value ?? '';
    });

    this.frequencyControl.valueChanges.subscribe((value) => {
      this.dispensationForm.frequency_of_intake = value ?? '';
    });

    this.durationControl.valueChanges.subscribe((value) => {
      this.dispensationForm.duration = value ?? '';
    });

    if (!this.requiresPrescriptionIdentifiers()) {
      this.retrieve();
    }
  }

  protected requiresPrescriptionIdentifiers(): boolean {
    const country = this.selectedCountry?.countryCode?.toLowerCase();

    return ['cz', 'pt', 'it'].includes(country);
  }

  protected requiresPinCode(): boolean {
    return this.selectedCountry?.countryCode?.toLowerCase() === 'pt';
  }

  protected retrieve = () => {
    this.clearDocumentView();
    this.errorMessage = '';
    this.list = [];
    this.loading = true;
    this.cdr.detectChanges();

    this.ncpbService
      .ep({
        countryCode: this.selectedCountry.countryCode,
        patientIdentifier: this.patient.identifier,
        purposeOfUse: 'TREATMENT',
        prescriptionId: this.formdata.prescriptionId,
        dispensationPinCode: this.formdata.dispensationPINCode,
      })
      .subscribe({
        next: (res: any[]) => {
          this.list = this.mapEpList(res || []);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.list = [];
          this.errorMessage = this.getErrorMessage(err);
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  };

  private mapEpList(epList: any[]): EpDocumentItem[] {
    const source = [...epList];
    const mapped: EpDocumentItem[] = [];

    for (let i = source.length - 1; i >= 0; i--) {
      const first = source.pop();
      const second = source.pop();

      if (!first && !second) {
        continue;
      }

      if (first && second) {
        const item: EpDocumentItem = {
          ...first,
          ...second,
          clinicalDocuments: first.clinicalDocuments || second.clinicalDocuments || [],
        };

        if (first.clinicalDocuments?.[0] && second.clinicalDocuments?.[0]) {
          item.clinicalDocuments[0].identifier_1 = first.clinicalDocuments[0].identifier;

          item.clinicalDocuments[0].identifier_2 = second.clinicalDocuments[0].identifier;
        }

        mapped.push(item);
        i--;
      } else {
        mapped.push(first || second);
      }
    }

    return mapped.sort(
      (a, b) =>
        new Date(b.associateDocuments?.[0]?.creationDate || '').getTime() -
        new Date(a.associateDocuments?.[0]?.creationDate || '').getTime()
    );
  }

  protected printPdf = (item: EpDocumentItem) => {
    const document = item.clinicalDocuments?.[0];

    if (!document) {
      return;
    }

    this.clearDocumentView();
    this.documentLoading = true;
    this.cdr.detectChanges();
    this.scrollToReports();

    this.ncpbService
      .epRetrieve({
        countryCode: this.selectedCountry.countryCode,
        nextOfKin: item.nextOfKin,
        patientIdentifier: item.patientIdentifier,
        purposeOfUse: 'TREATMENT',
        repositoryId: document.repositoryId,
        documentIdentifier: document.identifier_2 || document.identifier,
        homeCommunityId: document.homeCommunityId,
        prescriptionId: this.formdata.prescriptionId,
        dispensationPinCode: this.formdata.dispensationPINCode,
      })
      .subscribe({
        next: (res: any) => {
          const pdfContent = res?.pdfContent || res?.content;

          if (pdfContent) {
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:application/pdf;base64,${pdfContent}`
            );
          }

          this.documentLoading = false;
          this.cdr.detectChanges();
          this.scrollToReports();
        },
        error: (err) => {
          this.errorMessage = this.getErrorMessage(err);
          this.documentLoading = false;
          this.cdr.detectChanges();
        },
      });
  };

  protected displayCda = (item: EpDocumentItem) => {
    const document = item.clinicalDocuments?.[0];

    if (!document) {
      return;
    }

    this.clearDocumentView();
    this.documentLoading = true;
    this.cdr.detectChanges();
    this.scrollToReports();

    this.ncpbService
      .epRetrieve({
        countryCode: this.selectedCountry.countryCode,
        nextOfKin: item.nextOfKin,
        patientIdentifier: item.patientIdentifier,
        purposeOfUse: 'TREATMENT',
        repositoryId: document.repositoryId,
        documentIdentifier: document.identifier_1 || document.identifier,
        homeCommunityId: document.homeCommunityId,
        prescriptionId: this.formdata.prescriptionId,
        dispensationPinCode: this.formdata.dispensationPINCode,
      })
      .subscribe({
        next: (retrieveResponse: any) => {
          this.dispensationContext = {
            cda: retrieveResponse.content,
            homeCommunityId: retrieveResponse.homeCommunityId,
            repositoryId: retrieveResponse.repositoryId,
            patientIdentifier: this.patient.identifier,
            purposeOfUse: 'TREATMENT',
          };

          this.resetDispensationForm(retrieveResponse);

          this.ncpbService
            .epDisplay({
              countryCode: this.selectedCountry.countryCode,
              nextOfKin: item.nextOfKin,
              patientIdentifier: item.patientIdentifier,
              purposeOfUse: 'TREATMENT',
              repositoryId: document.repositoryId,
              documentIdentifier: document.identifier_1 || document.identifier,
              homeCommunityId: document.homeCommunityId,
              clinicalDocumentContent: {
                content: retrieveResponse.content,
              },
            })
            .subscribe({
              next: (html: string) => {
                const blob = new Blob([html], {
                  type: 'text/html',
                });

                const url = URL.createObjectURL(blob);

                this.htmlUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                this.showDispensationForm = true;
                this.loadPharmacies();
                this.documentLoading = false;
                this.cdr.detectChanges();
                this.scrollToReports();
              },
              error: (err) => {
                this.errorMessage = this.getErrorMessage(err);
                this.documentLoading = false;
                this.cdr.detectChanges();
              },
            });
        },
        error: (err) => {
          this.errorMessage = this.getErrorMessage(err);
          this.documentLoading = false;
          this.cdr.detectChanges();
        },
      });
  };

  protected onPharmacySelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.pharmacy_id =
      selected.id || selected.pharmacy_id || selected.organization_id || '';
  };

  private resetDispensationForm(retrieveResponse: any) {
    this.productSelectedItem = {};

    this.setDropdownData('brandNameProducts', []);

    this.productDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationBrandName',
      placeholder: 'default',
      name: 'brandNameProducts',
      label: {
        id: 'dispensationBrandNameLabel',
        text: 'brand_name',
      },
      data: [],
      initialData: [],
      required: true,
      searchComponent: BrandNameSearchComponent,
    });

    this.dispensationForm = {
      productNameDispensation: '',
      atcCode: retrieveResponse.atcCode || '',
      atcDescription: retrieveResponse.atcDescription || '',
      atcCodeName: retrieveResponse.atcCode
        ? `${retrieveResponse.atcCode} | ${retrieveResponse.atcDescription || ''}`
        : '',
      strength: '',
      drugid: '',
      productPackage: '',
      packnr: '',
      pharmacy_id: '',
      number_of_packages: '',
      quantity: '',
      quantity_unit: '',
      frequency_of_intake: '',
      frequency_of_intake_unit: '',
      duration: '',
      duration_unit: '',
      substitution: false,
    };

    this.numberOfPackagesControl.setValue('', { emitEvent: false });
    this.quantityControl.setValue('', { emitEvent: false });
    this.frequencyControl.setValue('', { emitEvent: false });
    this.durationControl.setValue('', { emitEvent: false });

    this.substitutionRadiosOptions.set(
      new RadiosOptions({
        id: 'dispensationSubstitution',
        options: [
          {
            value: 1,
            label: 'yes',
          },
        ],
        onInput: (value) => {
          this.dispensationForm.substitution = value === 1;
        },
      })
    );

    this.productSelectedItem = {};
    this.packageSelectedItem = {};
    this.quantityUnitSelectedItem = {};
    this.frequencyUnitSelectedItem = {};
    this.durationUnitSelectedItem = {};
    this.pharmacySelectedItem = {};

    this.setDropdownData('packageDescriptions', []);

    this.packageDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationPackageDescription',
      placeholder: 'default',
      name: 'packageDescriptions',
      label: {
        id: 'dispensationPackageDescriptionLabel',
        text: 'package_description',
      },
      data: [],
      required: true,
    });

    this.setDropdownData('quantityUnits', []);

    this.quantityUnitDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationQuantityUnit',
      placeholder: 'default',
      name: 'quantityUnits',
      label: {
        id: 'dispensationQuantityUnitLabel',
        text: 'units_intake_description',
      },
      data: [],
      required: true,
    });

    this.setDropdownData('frequencyUnits', []);

    this.frequencyUnitDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationFrequencyUnit',
      placeholder: 'default',
      name: 'frequencyUnits',
      label: {
        id: 'dispensationFrequencyUnitLabel',
        text: 'time_unit_description',
      },
      data: [],
      required: true,
    });

    this.setDropdownData('durationUnits', []);

    this.durationUnitDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationDurationUnit',
      placeholder: 'default',
      name: 'durationUnits',
      label: {
        id: 'dispensationDurationUnitLabel',
        text: 'time_unit_description',
      },
      data: [],
      required: true,
    });

    this.setDropdownData('pharmacies', []);

    this.pharmacyDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationPharmacy',
      placeholder: 'default',
      name: 'pharmacies',
      label: {
        id: 'dispensationPharmacyLabel',
        text: 'pharmacy',
      },
      data: [],
      required: true,
    });

    this.loadProductsForDispensation();
    this.showDispensationForm = false;
  }

  private setDropdownData(name: string, data: any[]) {
    const dropDownData = JSON.parse(localStorage.getItem('dropDownData') || '{}');

    dropDownData[name] = data;

    localStorage.setItem('dropDownData', JSON.stringify(dropDownData));
  }

  private loadProductsForDispensation() {
    const description = this.dispensationForm.atcCode || '';

    if (!description) {
      return;
    }

    this.apiService
      .call({
        path: '/get-products_atc',
        body: {
          description,
        },
      })
      ?.subscribe({
        next: (response: any) => {
          const data = (response?.data || []).map((x: any) => ({
            ...x,
            description: [x.productName, x.atcdescription, x.activeSubstance, x.strength, x.atc]
              .filter(Boolean)
              .join(' || '),
          }));

          this.setDropdownData('brandNameProducts', data);

          this.productDropdownOptions = new DropdownOptions<any>({
            id: 'dispensationBrandName',
            placeholder: 'default',
            name: 'brandNameProducts',
            label: {
              id: 'dispensationBrandNameLabel',
              text: 'brand_name',
            },
            data: [],
            required: true,
            searchComponent: BrandNameSearchComponent,
          });

          this.cdr.detectChanges();
        },
        error: () => {
          this.setDropdownData('brandNameProducts', []);

          this.productDropdownOptions = new DropdownOptions<any>({
            id: 'dispensationBrandName',
            placeholder: 'default',
            name: 'brandNameProducts',
            label: {
              id: 'dispensationBrandNameLabel',
              text: 'brand_name',
            },
            data: [],
            required: true,
            searchComponent: BrandNameSearchComponent,
          });

          this.cdr.detectChanges();
        },
      });
  }

  protected onProductSelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.productNameDispensation = selected.productName;
    this.dispensationForm.productPackage = selected.packageDescription || '';
    this.dispensationForm.strength = selected.strength || '';
    this.dispensationForm.drugid = selected.drugid;

    this.dispensationForm.atcCode = selected.atc || this.dispensationForm.atcCode;

    this.dispensationForm.atcDescription =
      selected.atcdescription || this.dispensationForm.atcDescription;

    this.dispensationForm.atcCodeName = `${this.dispensationForm.atcCode || ''} | ${this.dispensationForm.atcDescription || ''}`;

    this.dispensationForm.packnr = selected.packnr || '';

    this.getProductPackUnit(selected.drugid);
    this.getProductRoute(selected.drugid);
    this.punitfetchAll();
    this.unitsPerIntakeFetchAll();
    this.funitfetchAll();
    this.dunitfetchAll();
  };

  protected loadPharmacies = () => {
    this.ncpbService.getPharmacy().subscribe({
      next: (response: any) => {
        const data = (response?.data || response || []).map((x: any) => ({
          ...x,
          description: x.description || x.name || x.pharmacy_name || x.organization_name || x.id,
        }));

        this.setDropdownData('pharmacies', data);

        this.pharmacyDropdownOptions = new DropdownOptions<any>({
          id: 'dispensationPharmacy',
          placeholder: 'default',
          name: 'pharmacies',
          label: {
            id: 'dispensationPharmacyLabel',
            text: 'pharmacy',
          },
          data: [],
          required: true,
        });

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.log('PHARMACY ERROR', err);

        this.setDropdownData('pharmacies', []);

        this.pharmacyDropdownOptions = new DropdownOptions<any>({
          id: 'dispensationPharmacy',
          placeholder: 'default',
          name: 'pharmacies',
          label: {
            id: 'dispensationPharmacyLabel',
            text: 'pharmacy',
          },
          data: [],
          required: true,
        });

        this.cdr.detectChanges();
      },
    });
  };

  private getProductPackUnit(drugid: number) {
    this.apiService
      .call({
        path: '/get-product_pack_unit',
        body: {
          drugid,
        },
      })
      ?.subscribe((response: any) => {
        const item = response?.data?.[0];

        if (item?.packageSizeUnit) {
          this.dispensationForm.quantity_unit = item.packageSizeUnit;

          this.quantityUnitSelectedItem = {
            item: {
              description: item.packageSizeUnit,
            },
          };
        }
      });
  }

  private getProductRoute(drugid: number) {
    this.apiService
      .call({
        path: '/get-product_route',
        body: {
          drugid,
        },
      })
      ?.subscribe((response: any) => {
        const item = response?.data?.[0];

        if (item?.route) {
          this.dispensationForm.route_of_administration = item.route;
        }
      });
  }

  protected punitfetchAll = () => {
    if (!this.dispensationForm.drugid) {
      return;
    }

    this.apiService
      .call({
        path: '/get-product_pack_desc',
        body: {
          drugid: this.dispensationForm.drugid,
        },
      })
      ?.subscribe((response: any) => {
        const data = (response?.data || []).map((x: any) => ({
          ...x,
          description: x.packageDescription,
        }));

        this.setDropdownData('packageDescriptions', data);

        this.packageDropdownOptions = new DropdownOptions<any>({
          id: 'dispensationPackageDescription',
          placeholder: 'default',
          name: 'packageDescriptions',
          label: {
            id: 'dispensationPackageDescriptionLabel',
            text: 'package_description',
          },
          data: [],
          required: true,
        });

        this.cdr.detectChanges();
      });
  };

  protected onPackageSelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.productPackage = selected.packageDescription;

    this.dispensationForm.packnr = selected.packnr || selected.packageSize || '';
  };

  protected unitsPerIntakeFetchAll = () => {
    const initialQuantityUnit = this.dispensationForm.quantity_unit;

    this.apiService
      .call({
        path: '/get-quantity-unit-descriptions',
      })
      ?.subscribe({
        next: (response: any) => {
          const apiData = (response?.data || response || []).map((x: any) => ({
            ...x,
            description: x.description || x.displayName || x.display || x.name || x.code || '',
          }));

          const initialOption = initialQuantityUnit
            ? [
                {
                  description: initialQuantityUnit,
                },
              ]
            : [];

          const data = [
            ...initialOption,
            ...apiData.filter((x: any) => x.description !== initialQuantityUnit),
          ];

          this.setDropdownData('quantityUnits', data);

          this.quantityUnitDropdownOptions = new DropdownOptions<any>({
            id: 'dispensationQuantityUnit',
            placeholder: 'default',
            name: 'quantityUnits',
            label: {
              id: 'dispensationQuantityUnitLabel',
              text: 'units_intake_description',
            },
            data: [],
            required: true,
          });

          this.cdr.detectChanges();
        },
        error: (err) => {
          console.log(err);
        },
      });
  };

  protected onQuantityUnitSelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.quantity_unit = selected.description;
  };

  protected funitfetchAll = () => {
    const data = [
      { description: 'hour' },
      { description: 'day' },
      { description: 'week' },
      { description: 'month' },
    ];

    this.setDropdownData('frequencyUnits', data);

    this.frequencyUnitDropdownOptions = new DropdownOptions<any>({
      id: 'dispensationFrequencyUnit',
      placeholder: 'default',
      name: 'frequencyUnits',
      label: {
        id: 'dispensationFrequencyUnitLabel',
        text: 'time_unit_description',
      },
      data: [],
      required: true,
    });

    this.cdr.detectChanges();
  };

  protected onFrequencyUnitSelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.frequency_of_intake_unit = selected.description;
  };

  protected dunitfetchAll = () => {
    if (!this.dispensationForm.drugid) {
      return;
    }

    this.apiService
      .call({
        path: '/get-product_duration_unit',
        body: {
          drugid: this.dispensationForm.drugid,
        },
      })
      ?.subscribe((response: any) => {
        const data = (response?.data || []).map((x: any) => ({
          ...x,
          description: x.description || x.durationUnit || x.duration_unit || x.name,
        }));

        this.setDropdownData('durationUnits', data);

        this.durationUnitDropdownOptions = new DropdownOptions<any>({
          id: 'dispensationDurationUnit',
          placeholder: 'default',
          name: 'durationUnits',
          label: {
            id: 'dispensationDurationUnitLabel',
            text: 'time_unit_description',
          },
          data: [],
          required: true,
        });

        this.cdr.detectChanges();
      });
  };

  protected onDurationUnitSelected = (selectedItem: any) => {
    const selected = selectedItem?.item;

    if (!selected) {
      return;
    }

    this.dispensationForm.duration_unit = selected.description;
  };

  protected dispense = () => {
    this.dispensationResults = '';
    this.dispensationIsSuccess = false;

    this.dispensationForm.number_of_packages = this.numberOfPackagesControl.value;

    this.dispensationForm.quantity = this.quantityControl.value;

    this.dispensationForm.frequency_of_intake = this.frequencyControl.value;

    this.dispensationForm.duration = this.durationControl.value;

    const payload = {
      countryCode: this.selectedCountry.countryCode,
      cda: this.dispensationContext.cda,
      homeCommunityId: this.dispensationContext.homeCommunityId,
      repositoryId: this.dispensationContext.repositoryId,
      patientIdentifier: this.dispensationContext.patientIdentifier,
      purposeOfUse: this.dispensationContext.purposeOfUse,
      pharmacist_id: this.getPharmacistId(),
      pharmacy_id: this.dispensationForm.pharmacy_id,
      drugid: this.dispensationForm.drugid,
      packnr: this.dispensationForm.packnr,
      number_of_packages: this.dispensationForm.number_of_packages,
      quantity: this.dispensationForm.quantity,
      quantity_unit: this.dispensationForm.quantity_unit,
      frequency_of_intake: this.dispensationForm.frequency_of_intake,
      frequency_of_intake_unit: this.dispensationForm.frequency_of_intake_unit,
      duration: this.dispensationForm.duration,
      duration_unit: this.dispensationForm.duration_unit,
      substitution: !!this.dispensationForm.substitution,
    };

    if (
      !payload.cda ||
      !payload.pharmacist_id ||
      !payload.pharmacy_id ||
      !payload.drugid ||
      !payload.packnr ||
      !payload.number_of_packages ||
      !payload.quantity ||
      !payload.quantity_unit ||
      !payload.frequency_of_intake ||
      !payload.frequency_of_intake_unit ||
      !payload.duration ||
      !payload.duration_unit
    ) {
      this.dispensationResults = 'Please complete all required fields.';
      return;
    }

    this.dispenseIsRunning = true;
    this.cdr.detectChanges();

    this.ncpbService.epDispense(payload).subscribe({
      next: (res: any) => {
        this.dispensationIsSuccess =
          res?.status === 'urn:oasis:names:tc:ebxml-regrep:ResponseStatusType:Success';

        if (this.dispensationIsSuccess) {
          this.showDispensationForm = false;
          this.htmlUrl = undefined;
          this.dispensationResults = 'The dispensation has been successfully completed!';

          if (res?.dispenseId) {
            this.downloadEdDocument(payload.pharmacist_id, payload.pharmacy_id, res.dispenseId);
          } else {
            this.dispenseIsRunning = false;
          }
        } else {
          this.dispensationResults = res?.message || res?.status || 'Dispensation failed.';

          this.dispenseIsRunning = false;
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.dispensationResults = this.getErrorMessage(err);
        this.dispenseIsRunning = false;
        this.cdr.detectChanges();
      },
    });
  };

  private downloadEdDocument(userId: string, pharmacyId: string, edId: string): void {
    this.apiService
      .getEdDocument({
        user_id: userId.toString(),
        pharmacy_id: pharmacyId.toString(),
        eD_document_id: edId.toString(),
      })
      .subscribe({
        next: (res: any) => {
          try {
            if (!res?.data) return;

            const binary = atob(res.data);
            const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `eD_${edId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (err) {
            console.error('Failed to download eD document:', err);
          } finally {
            this.dispenseIsRunning = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Failed to retrieve eD document:', err);
          this.dispenseIsRunning = false;
          this.cdr.detectChanges();
        },
      });
  }

  protected getCreationDate(item: EpDocumentItem): string {
    return item.associateDocuments?.[0]?.creationDate || '';
  }

  protected getAuthor(item: EpDocumentItem): string {
    return item.authors?.[0]?.person || '';
  }

  protected getDescription(item: EpDocumentItem): string {
    return item.description || '';
  }

  protected getActiveSubstance(item: EpDocumentItem): string {
    return `${item.atcText || ''} ${item.strength || ''}`.trim();
  }

  protected canRetrieve(item: EpDocumentItem): boolean {
    return item.dispensable !== '0';
  }

  private getPharmacistId(): string {
    const profile = JSON.parse(sessionStorage.getItem('profile') || '{}');

    return (
      profile?.details?.UserId ||
      profile?.details?.userId ||
      profile?.UserId ||
      profile?.userId ||
      ''
    );
  }

  private clearDocumentView() {
    this.pdfUrl = undefined;
    this.htmlUrl = undefined;
    this.showDispensationForm = false;
    this.dispensationResults = '';
    this.documentLoading = false;
  }

  private scrollToReports() {
    setTimeout(() => {
      this.reportsSection?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  private getErrorMessage(err: any): string {
    return err?.error?.message || err?.error?.error || err?.message || 'Unknown error';
  }
}
